import { UserRoleEnum } from '@elearning-platform/api-types/enums';
import { Button, ButtonSize, ButtonTheme, Color, Icon, IconName, Loader } from '@elearning-platform/ui';
import React from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { Location, Navigate, useLocation } from 'react-router-dom';

import { RouteNameEnum } from '../../enums';
import { useAuth } from '../../hooks';
import { UseAuth } from '../../interfaces/hooks';
import { LangRouteInfo } from '../../interfaces/internal';
import { getRouteNameByUrl, getUrlByRouteName } from '../../utils';

import { Container, Description, LoaderContainer } from './checkAuth.styled';
import { CheckAuthProps } from './checkAuth.types';

export const CheckAuth: React.FC<CheckAuthProps> = (props: CheckAuthProps): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const { children }: CheckAuthProps = props;
  const location: Location = useLocation();
  const {
    isLoggedIn,
    userData,
    isAuthInitialized,
    hasUserRequiredFunctionalities,
    appSettings,
    changeCurrentMenuRole,
    isRouteAvailableForMenuRole,
  }: UseAuth = useAuth();
  const onlyUnauthenticatedPaths: string[] = [
    RouteNameEnum.SignIn,
    RouteNameEnum.Registration,
    RouteNameEnum.RemindPassword,
    RouteNameEnum.PreAccountRegistration,
  ];
  const currentPathKey: string = getRouteNameByUrl(location.pathname) ?? RouteNameEnum.NotFound;
  const defaultRouteUrl: string = getUrlByRouteName(RouteNameEnum.Dashboard)?.url ?? '/';

  const hasRoutePermission: (routeNameKey: string) => boolean = (routeNameKey: string): boolean => {
    if (routeNameKey === RouteNameEnum.NotFound) {
      return true;
    }

    const route: LangRouteInfo | undefined = getUrlByRouteName(routeNameKey as RouteNameEnum);
    if (!route || !userData?.availableRoutes) {
      return false;
    }

    return hasUserRequiredFunctionalities(route.requiredFunctionalities);
  };

  if (!isAuthInitialized) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  if (!isLoggedIn && getUrlByRouteName(currentPathKey as RouteNameEnum)?.secured) {
    return (
      <Navigate replace state={{ from: location }} to={getUrlByRouteName(RouteNameEnum.SignIn)?.url ?? '/'} />
    );
  }

  if (isLoggedIn) {
    if (onlyUnauthenticatedPaths.includes(currentPathKey)) {
      return (
        <Navigate replace state={{ from: location }} to={defaultRouteUrl} />
      );
    } else if (
      appSettings?.currentMenuRole
      && hasRoutePermission(currentPathKey) && isRouteAvailableForMenuRole(currentPathKey, appSettings.currentMenuRole)
    ) {
      return children;
    } else if (
      appSettings?.currentMenuRole
      && hasRoutePermission(currentPathKey) && !isRouteAvailableForMenuRole(currentPathKey, appSettings.currentMenuRole)
    ) {
      return (
        <Container>
          <Icon color={Color.Brand300} name={IconName.HiddenOutlined} size={100} />
          <Description>{t('competence.app.checkAuth.routeUnavailableForCurrentMenuRole')}</Description>
          <Button
            icon={IconName.Sync}
            label={t('competence.app.checkAuth.changeActiveRole')}
            onClick={(): void => changeCurrentMenuRole(
              (userData?.role.name && appSettings.currentMenuRole === UserRoleEnum.USER) ? userData.role.name : UserRoleEnum.USER
            )}
            size={ButtonSize.Medium}
            variant={ButtonTheme.SecondaryBrand}
          />
        </Container>
      );
    } else {
      return (
        <Container>
          <Icon color={Color.Red300} name={IconName.RemoveCircleOutlined} size={100} />
          <Description>{t('competence.app.checkAuth.accessDeniedDescription')}</Description>
        </Container>
      );
    }
  }

  return children;
};
